import { z } from "zod";
import { LIBRARY_SORTS, type LibraryFilterQuery, type LibraryItemsQuery, type LibrarySort, type SortOrder } from "../sources/types";
import { languageName } from "./mediaFormat";

/**
 * The library page's URL state (rago-server §4c on the wire): sort +
 * direction, a random seed, the title query, three flags and nine list
 * facets. Absent means off, so the plain library URL stays clean.
 *
 * Router quirk (router-core 1.171): every search value is JSON-parsed on
 * the way in ("1999" arrives as the number 1999, "true" as a boolean) and
 * a STRING that JSON-parses is re-quoted on the way out ("1999" would be
 * written as year=%221999%22). So the schema accepts string | number |
 * boolean and normalises, and joinList hands a lone all-digit value back
 * as a number so the URL reads year=1999.
 */

export const LIST_FACETS = [
  "genre",
  "year",
  "decade",
  "contentRating",
  "studio",
  "resolution",
  "videoRange",
  "audioLanguage",
  "subtitleLanguage",
] as const;
export type ListFacet = (typeof LIST_FACETS)[number];

export const FLAG_FILTERS = ["unwatched", "inProgress", "unmatched"] as const;
export type FlagFilter = (typeof FLAG_FILTERS)[number];

const text = z
  .union([z.string(), z.number(), z.boolean()])
  .optional()
  .catch(undefined)
  .transform((v) => (v == null || v === "" ? undefined : String(v)));
const flag = z
  .boolean()
  .optional()
  .catch(undefined)
  .transform((v) => (v ? (true as const) : undefined));

export const librarySearchSchema = z.object({
  sort: z.enum(LIBRARY_SORTS).default("title").catch("title"),
  order: z.enum(["asc", "desc"]).optional().catch(undefined),
  seed: z.number().int().nonnegative().optional().catch(undefined),
  q: text,
  unwatched: flag,
  inProgress: flag,
  unmatched: flag,
  genre: text,
  year: text,
  decade: text,
  contentRating: text,
  studio: text,
  resolution: text,
  videoRange: text,
  audioLanguage: text,
  subtitleLanguage: text,
});

/**
 * The validated search as the page reads it. Written by hand rather than
 * z.output: the transforms make every key required-but-undefined there,
 * and the toolbar builds partial objects.
 */
export interface LibrarySearch {
  sort: LibrarySort;
  order?: SortOrder;
  seed?: number;
  q?: string;
  unwatched?: true;
  inProgress?: true;
  unmatched?: true;
  genre?: string;
  year?: string;
  decade?: string;
  contentRating?: string;
  studio?: string;
  resolution?: string;
  videoRange?: string;
  audioLanguage?: string;
  subtitleLanguage?: string;
}

/** A list facet's values from the URL form: a number (the router's
 * coercion), a comma-joined string, or an array (a repeated key). Sorted
 * and deduped so two orderings of the same picks make one query key. */
export function parseList(v: unknown): string[] {
  const raw: string[] = Array.isArray(v) ? v.map(String) : v == null ? [] : [String(v)];
  const out = new Set<string>();
  for (const chunk of raw) {
    for (const part of chunk.split(",")) {
      const t = part.trim();
      if (t) out.add(t);
    }
  }
  return [...out].sort();
}

/** The URL form of a list: absent when empty, a number for a lone
 * all-digit value (see the router quirk above), else comma-joined. */
export function joinList(values: string[]): string | number | undefined {
  const list = parseList(values);
  if (list.length === 0) return undefined;
  if (list.length === 1 && /^\d+$/.test(list[0]!)) return Number(list[0]);
  return list.join(",");
}

/** Every key's natural direction: title and content rating read A→Z,
 * everything else newest / biggest first. Random has none. */
export function defaultOrderFor(sort: LibrarySort): SortOrder {
  return sort === "title" || sort === "contentRating" || sort === "random" ? "asc" : "desc";
}

export function orderOf(s: Pick<LibrarySearch, "sort" | "order">): SortOrder {
  return s.order ?? defaultOrderFor(s.sort);
}

export function newSeed(): number {
  return Math.floor(Math.random() * 2 ** 31);
}

/** Pick a sort: a new key takes its natural direction, the same key keeps
 * the current one; order is written only when it differs from the default;
 * random mints a seed (and keeps it while random stays selected). */
export function withSort(s: LibrarySearch, sort: LibrarySort, order?: SortOrder): LibrarySearch {
  const dir = order ?? (sort === s.sort ? orderOf(s) : defaultOrderFor(sort));
  return {
    ...s,
    sort,
    order: sort === "random" || dir === defaultOrderFor(sort) ? undefined : dir,
    seed: sort === "random" ? (s.sort === "random" ? (s.seed ?? newSeed()) : newSeed()) : undefined,
  };
}

export function toggleOrder(s: LibrarySearch): LibrarySearch {
  return withSort(s, s.sort, orderOf(s) === "asc" ? "desc" : "asc");
}

export function reshuffle(s: LibrarySearch): LibrarySearch {
  return s.sort === "random" ? { ...s, seed: newSeed() } : s;
}

export function listOf(s: LibrarySearch, facet: ListFacet): string[] {
  return parseList(s[facet]);
}

export function withList(s: LibrarySearch, facet: ListFacet, values: string[]): LibrarySearch {
  return { ...s, [facet]: joinList(values) as string | undefined };
}

export function toggleValue(s: LibrarySearch, facet: ListFacet, value: string): LibrarySearch {
  const current = listOf(s, facet);
  return withList(
    s,
    facet,
    current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
  );
}

export function withFlag(s: LibrarySearch, flag: FlagFilter, on: boolean): LibrarySearch {
  return { ...s, [flag]: on ? true : undefined };
}

export function withQuery(s: LibrarySearch, q: string): LibrarySearch {
  const t = q.trim();
  return { ...s, q: t || undefined };
}

/** The filter half as the wire shape (what the letter buckets take). */
export function toFilterQuery(s: LibrarySearch): LibraryFilterQuery {
  const out: LibraryFilterQuery = {};
  if (s.q) out.q = s.q;
  if (s.unmatched) out.unmatched = true;
  if (s.unwatched) out.unwatched = true;
  if (s.inProgress) out.inProgress = true;
  for (const facet of LIST_FACETS) {
    const list = listOf(s, facet);
    if (list.length) out[facet] = list;
  }
  return out;
}

/** The page query minus its window. */
export function toItemsQuery(s: LibrarySearch): Omit<LibraryItemsQuery, "offset" | "limit"> {
  const out: Omit<LibraryItemsQuery, "offset" | "limit"> = { sort: s.sort, ...toFilterQuery(s) };
  const order = orderOf(s);
  if (s.sort !== "random" && order !== defaultOrderFor(s.sort)) out.order = order;
  if (s.sort === "random") out.seed = s.seed ?? 0;
  return out;
}

/** A canonical string of the filters alone — order-independent, sort-free —
 * for query keys. */
export function filterKey(s: LibrarySearch): string {
  const parts: string[] = [];
  for (const flag of FLAG_FILTERS) if (s[flag]) parts.push(flag);
  if (s.q) parts.push(`q=${s.q}`);
  for (const facet of LIST_FACETS) {
    const list = listOf(s, facet);
    if (list.length) parts.push(`${facet}=${list.join(",")}`);
  }
  return parts.join("|");
}

/** The sort half for query keys. */
export function viewKey(s: LibrarySearch): string {
  return `${s.sort}/${orderOf(s)}${s.sort === "random" ? `/${s.seed ?? 0}` : ""}`;
}

export function activeFilterCount(s: LibrarySearch): number {
  let n = 0;
  for (const flag of FLAG_FILTERS) if (s[flag]) n++;
  if (s.q) n++;
  for (const facet of LIST_FACETS) n += listOf(s, facet).length;
  return n;
}

export function clearFilters(s: LibrarySearch): LibrarySearch {
  return { sort: s.sort, order: s.order, seed: s.seed };
}

const RESOLUTION_LABELS: Record<string, string> = { sd: "SD", "720p": "720p", "1080p": "1080p", "4k": "4K", "8k": "8K" };
const VIDEO_RANGE_LABELS: Record<string, string> = { sdr: "SDR", hdr: "HDR", dv: "Dolby Vision" };

/** A facet value as a person reads it: 4k → 4K, dv → Dolby Vision,
 * eng → English, 1990 (decade) → 1990s. */
export function facetValueLabel(facet: ListFacet, value: string): string {
  switch (facet) {
    case "resolution":
      return RESOLUTION_LABELS[value] ?? value;
    case "videoRange":
      return VIDEO_RANGE_LABELS[value] ?? value;
    case "audioLanguage":
    case "subtitleLanguage":
      return languageName(value) ?? (value === "und" ? "Unknown" : value);
    case "decade":
      return `${value}s`;
    default:
      return value;
  }
}

export interface AppliedChip {
  id: string;
  label: string;
  remove: (s: LibrarySearch) => LibrarySearch;
}

const FLAG_LABELS: Record<FlagFilter, string> = {
  unwatched: "Unwatched",
  inProgress: "In progress",
  unmatched: "Unmatched",
};

/** One removable chip per applied filter, in a stable order. */
export function appliedChips(s: LibrarySearch): AppliedChip[] {
  const out: AppliedChip[] = [];
  if (s.q) {
    out.push({ id: "q", label: `“${s.q}”`, remove: (x) => withQuery(x, "") });
  }
  for (const flag of FLAG_FILTERS) {
    if (s[flag]) out.push({ id: flag, label: FLAG_LABELS[flag], remove: (x) => withFlag(x, flag, false) });
  }
  for (const facet of LIST_FACETS) {
    for (const value of listOf(s, facet)) {
      out.push({
        id: `${facet}:${value}`,
        label: facetValueLabel(facet, value),
        remove: (x) => toggleValue(x, facet, value),
      });
    }
  }
  return out;
}

export const SORT_LABELS: Record<LibrarySort, string> = {
  title: "Title",
  added: "Date added",
  year: "Release year",
  released: "Release date",
  played: "Last played",
  playCount: "Play count",
  userRating: "Your rating",
  rating: "Rating",
  contentRating: "Content rating",
  duration: "Duration",
  resolution: "Resolution",
  bitrate: "Bitrate",
  random: "Random",
  lastEpisodeAdded: "Last episode added",
};

/** The sort menu's order; show libraries add their episode-based key. */
export function sortsFor(libraryType: "movie" | "show" | undefined): LibrarySort[] {
  const base: LibrarySort[] = [
    "title",
    "year",
    "released",
    "added",
    "played",
    "playCount",
    "rating",
    "userRating",
    "contentRating",
    "duration",
    "resolution",
    "bitrate",
    "random",
  ];
  if (libraryType === "show") base.splice(4, 0, "lastEpisodeAdded");
  return base;
}

/** Decade options from the years facet: floor to the decade, sum counts,
 * newest first. */
export function decadesOf(years: Array<{ value: number; count: number }>): Array<{ value: string; count: number }> {
  const sums = new Map<number, number>();
  for (const y of years) {
    const d = Math.floor(y.value / 10) * 10;
    sums.set(d, (sums.get(d) ?? 0) + y.count);
  }
  return [...sums.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([d, count]) => ({ value: String(d), count }));
}
